import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";
import { universalAskMeridianAction } from "@/lib/actions/definitions/universal-actions";

// ============================================================================
// RECURRING TRANSACTION DETAIL PAGE CONFIGURATION
// ============================================================================

export const ENHANCED_RECURRING_TRANSACTION_DETAIL_CONFIG: DetailPageConfig<RecurringTransaction> =
  {
    entity: "recurring-transactions",
    displayName: "Recurring Transaction",

    actions: [
      // AI ASSISTANT ACTIONS — focused Meridian chat about this recurring transaction
      universalAskMeridianAction<RecurringTransaction>(),
      {
        id: "view-details",
        label: "View Details",
        icon: "eye",
        variant: "outline",
        action: "viewDetails",
        allowedRoles: ["wtc_admin", "wtc_employee"],
        primary: true,
      },
      {
        id: "edit",
        label: "Edit",
        icon: "edit",
        variant: "outline",
        action: "openEditModal",
        allowedRoles: ["wtc_admin"],
        primary: true,
      },
      {
        id: "duplicate",
        label: "Duplicate",
        icon: "copy",
        variant: "outline",
        action: "duplicateItem",
        allowedRoles: ["wtc_admin"],
      },
      {
        id: "viewCustomer",
        label: "View Customer",
        icon: "user",
        variant: "outline",
        action: "viewContact",
        allowedRoles: ["wtc_admin", "wtc_employee"],
      },
      {
        id: "view-company",
        label: "View Company",
        icon: "building",
        variant: "outline",
        action: "viewCompany",
        allowedRoles: ["wtc_admin", "wtc_employee"],
      },
      {
        // Opens the customer's Slack channel (resolved server-side from the
        // transaction's Hub company_id → companies.slack_channel_id). Shows a
        // toast when no channel is set rather than opening a dead tab.
        id: "open-slack-channel",
        label: "Open Slack Channel",
        icon: "message-square",
        variant: "outline",
        action: "openSlackChannel",
        allowedRoles: ["wtc_admin", "wtc_employee"],
        tooltip: "Open this customer's Slack channel to start a conversation.",
      },
      {
        id: "copy-link",
        label: "Copy Link",
        icon: "link",
        variant: "outline",
        action: "copyLink",
        allowedRoles: ["wtc_admin", "wtc_employee"],
      },
      {
        id: "delete",
        label: "Delete",
        icon: "trash",
        variant: "destructive",
        action: "deleteItem",
        allowedRoles: ["wtc_admin"],
      },
    ],

    tabs: [
      // Core Information Group
      {
        id: "details",
        label: "Details",
        icon: "info",
        variant: "blue",
        group: "core",
        priority: 1,
        content: {
          type: "fields",
          sections: [
            {
              id: "main",
              title: "Transaction Details",
              fields: [
                "customer_id",
                "company_id",
                "item",
                "description",
                "amount",
                "quantity",
                "sync_status",
                "last_synced_at",
                "last_synced_month",
                "quickbooks_id",
                "quickbooks_realm_id",
                "created_at",
                "updated_at",
              ],
            },
          ],
        },
      },
      // Related Entities Group
      {
        id: "contact",
        label: "Contact",
        icon: "user",
        variant: "blue",
        group: "related",
        priority: 1,
        visible: (item: RecurringTransaction) => !!item.customer_id,
        content: {
          type: "custom",
          customComponent: "RecurringTransactionContactDisplay",
        },
      },
      {
        id: "company",
        label: "Company",
        icon: "building",
        variant: "red",
        group: "related",
        priority: 2,
        visible: (item: RecurringTransaction) => !!(item.company_id || item.company_name),
        content: {
          type: "custom",
          customComponent: "RecurringTransactionCompanyDisplay",
        },
      },
      {
        id: "quickbooks",
        label: "QuickBooks",
        icon: "zap",
        variant: "green",
        group: "related",
        priority: 3,
        visible: (item: RecurringTransaction) => !!(item.customer_id || item.company_id),
        content: {
          type: "custom",
          customComponent: "RecurringTransactionQuickBooksDisplay",
        },
      },
    ],
  };
